/**
 * MyPurchases - Display user's purchased scripts
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { doc, getDoc } from 'firebase/firestore';
import toast from 'react-hot-toast';
import { db, functions, getArtifactProjectId } from '../../firebaseInit';
import { useMarketplace } from '../../contexts/MarketplaceContext';
import { useAuth } from '../../contexts/AuthContext';
import Card from '../shared/Card';
import Button from '../shared/Button';
import LoadingSpinner from '../shared/LoadingSpinner';
import EmptyState from '../shared/EmptyState';

const MyPurchases = () => {
  const navigate = useNavigate();
  const { myPurchases, purchasesLoading, hasMarketplaceAccess, categories, initializeMarketplace, loadMyPurchases, submitReview } = useMarketplace();
  const { userTeam } = useAuth();
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [selectedPurchase, setSelectedPurchase] = useState(null);
  const [deletedTemplates, setDeletedTemplates] = useState(new Set());
  const [readdingId, setReaddingId] = useState(null);
  const attemptedRecoveryRef = useRef(new Set());
  const checkedTemplatesRef = useRef(new Set());

  // Initialize marketplace data on mount, and always load purchases when this tab mounts
  useEffect(() => {
    initializeMarketplace();
    // Always explicitly load purchases when this component mounts,
    // in case the initial marketplace initialization failed or returned stale data
    loadMyPurchases();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Check which purchased templates still exist in user's scripts
  const checkTemplateExistence = useCallback(async () => {
    if (!userTeam || purchasesLoading || myPurchases.length === 0) return;
    
    const appId = getArtifactProjectId();
    const deleted = new Set();
    
    for (const purchase of myPurchases) {
      if (!purchase.copiedTemplateId) continue;
      // Skip if already checked this template
      if (checkedTemplatesRef.current.has(purchase.copiedTemplateId)) {
        if (deletedTemplates.has(purchase.copiedTemplateId)) {
          deleted.add(purchase.copiedTemplateId);
        }
        continue;
      }
      
      try {
        const templateRef = doc(db, `artifacts/${appId}/teams/${userTeam}/templates/${purchase.copiedTemplateId}`);
        const templateSnap = await getDoc(templateRef);
        checkedTemplatesRef.current.add(purchase.copiedTemplateId);
        if (!templateSnap.exists()) {
          deleted.add(purchase.copiedTemplateId);
        }
      } catch (err) {
        // If we can't check, assume it exists
        console.warn('Could not check template existence:', err.message);
      }
    }
    
    if (deleted.size > 0) {
      setDeletedTemplates(deleted);
    }
  }, [userTeam, purchasesLoading, myPurchases, deletedTemplates]);

  useEffect(() => {
    checkTemplateExistence();
  }, [checkTemplateExistence]);

  // Auto-recover purchases that failed to copy
  useEffect(() => {
    const recoverFailedCopies = async () => {
      if (!userTeam || purchasesLoading) return;
      
      const failedPurchases = myPurchases.filter(
        p => p.status === 'completed' && !p.copiedTemplateId && !attemptedRecoveryRef.current.has(p.id)
      );
      
      if (failedPurchases.length === 0) return;
      
      console.log(`🔄 Attempting to recover ${failedPurchases.length} failed template copies...`);
      
      for (const purchase of failedPurchases) {
        attemptedRecoveryRef.current.add(purchase.id);
        
        try {
          const copyTemplate = httpsCallable(functions, 'copyTemplateForBuyer');
          await copyTemplate({
            listingId: purchase.listingId,
            templateId: purchase.templateId || purchase.listingId,
            buyerTeamId: userTeam
          });
          console.log(`✅ Recovered template for purchase: ${purchase.listingTitle}`);
        } catch (err) {
          console.warn(`⚠️ Could not recover template for ${purchase.listingTitle}:`, err.message);
        }
      }
      
      // Refresh to show updated status
      await loadMyPurchases();
    };
    
    recoverFailedCopies();
  }, [myPurchases, purchasesLoading, userTeam, loadMyPurchases]);

  // Access check
  if (!hasMarketplaceAccess) {
    return (
      <div className="p-4">
        <EmptyState
          icon="🔒"
          title="Access Restricted"
          description="The Script Marketplace is available to Team Leads and Admins only."
          action={
            <Button variant="primary" onClick={() => navigate('/')}>
              Go to Dashboard
            </Button>
          }
        />
      </div>
    );
  }

  const handleReaddToScripts = async (purchase) => {
    setReaddingId(purchase.id);
    try {
      const copyTemplate = httpsCallable(functions, 'copyTemplateForBuyer');
      const result = await copyTemplate({
        listingId: purchase.listingId,
        templateId: purchase.templateId || purchase.listingId,
        buyerTeamId: userTeam
      });
      
      if (result.data?.success) {
        toast.success(`"${purchase.listingTitle}" has been re-added to your scripts.`);
        // Clear from deleted set and reset check cache so it re-checks
        setDeletedTemplates(prev => {
          const next = new Set(prev);
          next.delete(purchase.copiedTemplateId);
          return next;
        });
        checkedTemplatesRef.current.delete(purchase.copiedTemplateId);
        // Refresh purchases to get updated copiedTemplateId
        await loadMyPurchases();
      } else {
        throw new Error('Copy failed');
      }
    } catch (err) {
      console.error('Error re-adding script:', err);
      const msg = err?.message || '';
      toast.error(
        msg.includes('not found') || msg.includes('not-found')
          ? 'Could not locate the original template. It may have been removed by the seller.'
          : msg.includes('permission-denied')
            ? 'Purchase verification failed. Please try refreshing the page.'
            : 'Failed to re-add script. Please try again.'
      );
    } finally {
      setReaddingId(null);
    }
  };

  const handleWriteReview = (purchase) => {
    setSelectedPurchase(purchase);
    setShowReviewModal(true);
  };

  return (
    <div className="w-full">
      {/* Sub-header */}
      <div className="flex items-center justify-between mb-6">
        <p className="text-gray-600 text-sm">
          Scripts you've acquired from the marketplace
        </p>
        <Button
          variant="outline"
          size="sm"
          onClick={() => loadMyPurchases()}
        >
          Refresh
        </Button>
      </div>

      {/* Loading state */}
      {purchasesLoading && myPurchases.length === 0 && (
        <div className="flex justify-center py-12">
          <LoadingSpinner size="large" message="Loading purchases..." />
        </div>
      )}

      {/* Empty state */}
      {!purchasesLoading && myPurchases.length === 0 && (
        <EmptyState
          icon="🛒"
          title="No purchases yet"
          description="Scripts you purchase from the marketplace will appear here."
          action={
            <Button variant="primary" onClick={() => navigate('/marketplace')}>
              Browse Marketplace
            </Button>
          }
        />
      )}

      {/* Purchases list */}
      {myPurchases.length > 0 && (
        <div className="space-y-4">
          {myPurchases.map(purchase => (
            <Card key={purchase.id} padding="md">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-gray-900 truncate" title={purchase.listingTitle}>
                    {purchase.listingTitle}
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Purchased {purchase.purchasedAt?.toDate?.()?.toLocaleDateString() || 'Unknown date'}
                  </p>
                  <p className="text-sm text-gray-500">
                    Created by {purchase.sellerDisplayName || purchase.sellerEmail}
                  </p>
                </div>
                
                <div className="flex-shrink-0 text-right">
                  {purchase.price === 0 ? (
                    <span className="text-green-600 font-medium">Free</span>
                  ) : (
                    <span className="text-gray-900 font-medium">${purchase.price}</span>
                  )}
                  
                  <div className="flex gap-2 mt-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/marketplace/listing/${purchase.listingId}`)}
                    >
                      View
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => handleWriteReview(purchase)}
                    >
                      Review
                    </Button>
                  </div>
                </div>
              </div>
              
              {/* Status badges */}
              <div className="mt-3 flex gap-2 items-center flex-wrap">
                {purchase.copiedTemplateId && deletedTemplates.has(purchase.copiedTemplateId) ? (
                  <>
                    <span className="inline-flex items-center px-2 py-0.5 text-xs bg-orange-100 text-orange-700 rounded">
                      Script removed from your templates
                    </span>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => handleReaddToScripts(purchase)}
                      disabled={readdingId === purchase.id}
                      className="text-xs"
                    >
                      {readdingId === purchase.id ? 'Re-adding...' : 'Re-add to Scripts'}
                    </Button>
                  </>
                ) : purchase.copiedTemplateId ? (
                  <>
                    <span className="inline-flex items-center px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded">
                      ✓ Added to your scripts
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        window.dispatchEvent(new CustomEvent('navigateTo', {
                          detail: { target: 'myScripts', url: `/my-scripts?tab=edit&template=${purchase.copiedTemplateId}` }
                        }));
                      }}
                      className="text-xs"
                    >
                      Open in Script Editor →
                    </Button>
                  </>
                ) : (
                  <span className="inline-flex items-center px-2 py-0.5 text-xs bg-yellow-100 text-yellow-700 rounded">
                    ⏳ Adding to your scripts...
                  </span>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Review Modal */}
      {showReviewModal && selectedPurchase && (
        <ReviewModal
          purchase={selectedPurchase}
          onClose={() => {
            setShowReviewModal(false);
            setSelectedPurchase(null);
          }}
          onSubmit={async (reviewData) => {
            try {
              await submitReview(
                selectedPurchase.listingId,
                selectedPurchase.id,
                reviewData
              );
              setShowReviewModal(false);
              setSelectedPurchase(null);
              // Refresh purchases to update review status
              await loadMyPurchases();
            } catch (err) {
              console.error('Error submitting review:', err);
              throw err;
            }
          }}
        />
      )}
    </div>
  );
};

// Simple review modal component
const ReviewModal = ({ purchase, onClose, onSubmit }) => {
  const [rating, setRating] = useState(5);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      await onSubmit({ rating, title, body });
    } catch (err) {
      console.error('Error submitting review:', err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" role="presentation" onKeyDown={(e) => { if (e.key === 'Escape') onClose(); }}>
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full m-4 p-4" role="dialog" aria-modal="true" aria-labelledby="review-dialog-title">
        <h3 id="review-dialog-title" className="text-lg font-semibold text-gray-900 mb-4">
          Review: {purchase.listingTitle}
        </h3>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Rating */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Rating
            </label>
            <div className="flex gap-2">
              {[1, 2, 3, 4, 5].map(star => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setRating(star)}
                  className={`text-2xl transition-colors ${
                    star <= rating ? 'text-yellow-500' : 'text-gray-300'
                  }`}
                >
                  ★
                </button>
              ))}
            </div>
          </div>

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Review Title (optional)
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Summarize your experience"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Body */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Written Review <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Share your experience with this script..."
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={submitting}
            >
              {submitting ? 'Submitting...' : body.trim() ? 'Submit Review' : 'Submit Rating'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MyPurchases;
